import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { GroupHeader } from "../components/Panel";
import { NewMilestoneButton } from "../components/NewMilestoneButton";
import { createMilestone } from "../data/api";
import { MilestoneStatusBadge, readinessLabel } from "../components/MilestoneStatus";
import { NEXT_TONE_CLASS } from "../data/status";
// The team's own day, not the viewer's: a milestone is overdue on Sao Paulo
// time like every other date in this app.
import { todayIso } from "../../../src/lib/dates";
import type { MilestonesData } from "../data/loaders";
import type { MilestoneSummary } from "../data/types";

const GRID = "grid grid-cols-[minmax(0,1fr)_150px_130px_110px_150px] items-center gap-3 px-4";

/** Date bands, worst first: the point of the screen is what is already wrong
 *  or nearly due, not a calendar. */
const BANDS = ["Overdue", "Next 30 days", "Later", "Past"] as const;
type Band = (typeof BANDS)[number];

function bandOf(m: MilestoneSummary, today: string): Band {
  if (m.status !== "planned") return "Past";
  if (m.eventDate < today) return "Overdue";
  const in30 = new Date(`${today}T00:00:00Z`);
  in30.setUTCDate(in30.getUTCDate() + 30);
  return m.eventDate <= in30.toISOString().slice(0, 10) ? "Next 30 days" : "Later";
}

export function Milestones() {
  const { milestones, accounts } = useLoaderData() as MilestonesData;
  const revalidator = useRevalidator();
  const today = todayIso();
  const banded = BANDS.map((b) => [b, milestones.filter((m) => bandOf(m, today) === b)] as const).filter(([, list]) => list.length > 0);

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-display-xs font-semibold text-gray-900">Milestones</h1>
          <p className="mt-1 text-sm text-[var(--text-2)]">Dates a customer is counting on, and the requests that have to land before them.</p>
        </div>
        <NewMilestoneButton
          accounts={accounts}
          onCreated={async (input) => {
            await createMilestone(input);
            revalidator.revalidate();
          }}
        />
      </header>

      {milestones.length === 0 ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg)] p-12 text-center">
          <h3 className="text-md font-medium text-gray-900">No milestones yet</h3>
          <p className="mt-1 text-sm text-[var(--text-2)]">Add one from an account to start tracking what has to be ready by when.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg)]">
          <div className={`${GRID} border-b border-[var(--border)] bg-gray-100 py-2 text-xs font-medium text-[var(--text-2)]`}>
            <span>Milestone</span>
            <span>Account</span>
            <span>Date</span>
            <span>Status</span>
            <span className="text-right">Readiness</span>
          </div>
          {banded.map(([band, list]) => (
            <div key={band}>
              <GroupHeader label={band} count={list.length} tone={band === "Overdue" ? "overdue" : "neutral"} />
              {list.map((m) => (
                <Link
                  key={m.id}
                  to={`/milestones/${m.id}`}
                  className={`${GRID} border-b border-[var(--border)] py-3 text-sm transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{m.title}</span>
                    <span className="block truncate text-xs text-[var(--text-3)]">{m.id}</span>
                  </span>
                  <span className="truncate text-[var(--text-2)]">{m.account}</span>
                  <span className={`text-xs ${NEXT_TONE_CLASS[m.readiness.dateTone]}`}>{m.readiness.dateLabel}</span>
                  <MilestoneStatusBadge status={m.status} />
                  <span className="text-right text-xs text-[var(--text-2)]">{readinessLabel(m.readiness)}</span>
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
